import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { ElementType } from "react";
import { Focusable } from "react-aria-components";
import { cn } from "@core/utils";
import { Tooltip } from "./Tooltip";

interface TruncatedTextProps {
  /** The full string. Also what the tooltip shows when it is clipped. */
  children: string;
  /**
   * How to clip. `1` uses `truncate` (single line, ellipsis); anything higher uses `line-clamp-N`.
   */
  lines?: 1 | 2 | 3;
  /** The element to render. A heading needs its own tag; the default is a span. */
  as?: ElementType;
  /**
   * Whether this text may own a focus stop.
   *
   * ⚠ SET FALSE INSIDE AN ALREADY-FOCUSABLE PARENT — a row button, a link. React Aria's tooltip
   * needs a focusable trigger, and nesting one inside a button is invalid and traps keyboard users
   * in a control that has two stops for one thing. In that case the parent's accessible name
   * already carries the full string, so nothing is lost to a screen reader and the clipping is
   * purely visual: this falls back to the native `title`, still only when actually clipped.
   */
  focusable?: boolean;
  className?: string;
}

/** `line-clamp-N` is not composable from a template string — Tailwind needs the literal class. */
const CLAMP = {
  1: "truncate",
  2: "line-clamp-2",
  3: "line-clamp-3",
} as const;

/**
 * Text that is clipped to a line count, and reveals itself on hover or focus WHEN IT IS ACTUALLY
 * CLIPPED.
 *
 * ⚠ THE MEASUREMENT IS THE WHOLE POINT. Attaching a tooltip to everything that *might* overflow is
 * worse than attaching none: most rows fit, so most tooltips would repeat text already fully on
 * screen, and an administrator quickly learns to ignore all of them — including the ones covering
 * a name that really was cut. So the element is measured and the tooltip only exists when the
 * content genuinely does not fit.
 *
 * ⚠ MEASURED AGAIN ON RESIZE, because whether a string fits is a function of the container, and this
 * app's content well gets NARROWER from `md` to `lg` (the 280px sidebar arrives at `lg` — see
 * rules/responsive.md). A row that fits on a tablet can clip on the laptop after it, so a
 * measurement taken once at mount is wrong for half the breakpoints.
 *
 * Wrapped in `Focusable` so the tooltip is reachable by keyboard: React Aria needs a focusable
 * trigger, and a `<span>` is not one. That also gives the clipped text a tab stop, which is correct —
 * if the only way to read it is the tooltip, it must be possible to reach it without a mouse.
 */
export function TruncatedText({
  children,
  lines = 1,
  as: Component = "span",
  focusable = true,
  className,
}: TruncatedTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [isClipped, setClipped] = useState(false);

  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    // Single line clips horizontally, a clamp clips vertically. Checking both keeps one component
    // honest for either mode; the +1 absorbs sub-pixel rounding, which otherwise reports a
    // permanent one-pixel overflow on perfectly fitting text.
    setClipped(
      node.scrollWidth > node.clientWidth + 1 ||
        node.scrollHeight > node.clientHeight + 1
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    const node = ref.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
    // `children` is a dependency because the same node with new text clips differently.
  }, [measure, children]);

  if (!focusable) {
    return (
      <Component
        ref={ref}
        className={cn(CLAMP[lines], "min-w-0", className)}
        // Native, and only when clipped — same measurement, no second focus stop. See `focusable`.
        title={isClipped ? children : undefined}
      >
        {children}
      </Component>
    );
  }

  const element = (
    <Component ref={ref} className={cn(CLAMP[lines], "min-w-0", className)}>
      {children}
    </Component>
  );

  /**
   * ⚠ THE WRAPPER IS CONDITIONAL, not the tooltip's `isDisabled`.
   *
   * `Focusable` makes whatever it wraps a tab stop, so wrapping unconditionally would give every
   * label on the page a focus stop — including the ones that fit, where tabbing to them reveals
   * nothing. Rendering the bare element until it is actually clipped keeps the keyboard path as
   * short as the screen is.
   *
   * The ref sits on `Component` in both branches, so the measurement that decides this keeps
   * running either way.
   */
  if (!isClipped) return element;

  return (
    <Tooltip content={children} delay={0}>
      <Focusable>{element}</Focusable>
    </Tooltip>
  );
}
